import { useTranslation } from "react-i18next";
import styles from "./style.module.scss";
import React, { useState, useEffect, useMemo } from "react";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import classNames from "classnames";
import {
  getOrganizationTiers,
  addBulkOrganizationPaymentTypeFee,
  updateOrganizationPaymentTypeFees,
  updateFeeTemplate,
} from "../../../../../setup/api/services/organization";
import {
  IOrganizationFeeTier,
  IOrganizationFeeType,
  IOrganizationPaymentBrand,
  IOrganizationPaymentFee,
  IBaseApiResponse,
} from "../../../../../setup/api/services/organization/models";
import { EPaymentBrand } from "../../../../../setup/api/services/contract/models";
import { matchPath, useLocation } from "react-router-dom";
import { ROUTES } from "../../../../../setup/constants";
import BusinessTable, {
  RowWithOptionalClick,
} from "../../../../../components/table";
import TextInput, {
  InputType,
} from "../../../../../components/forms/textInput/textInput";
import PrimaryButton, {
  PrimaryButtonType,
  PrimaryButtonSize,
} from "../../../../../components/common/PrimaryButton/PrimaryButton";

interface TiersInformationProps {
  organization: IOrganizations | undefined;
  isEditable?: boolean;
  onTiersChange?: (tiers: TierInfo[]) => void;
  onSave?: (tiers: TierInfo[]) => void;
}

// Define tier information structure
interface TierInfo extends RowWithOptionalClick {
  id?: number;
  name: string;
  from: number | string;
  to: number | string;
  bankTransfer: string;
  visa: string;
  mastercard: string;
  amex: string;
  mada: string;
  tabby: string;
  neo: string;
  tamara: string;
  rawPaymentTypes?: IOrganizationPaymentFee[]; // Store original payment types for ID tracking
}

const TiersInformation = ({
  organization,
  isEditable,
  onTiersChange,
  onSave,
}: TiersInformationProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [initialTiers, setInitialTiers] = useState<TierInfo[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [templateId, setTemplateId] = useState<number | undefined>();
  const [templateMetadata, setTemplateMetadata] = useState<{
    name: string;
    description: string;
    active: boolean;
  } | null>(null);
  const [initialMetadata, setInitialMetadata] = useState<{
    name: string;
    description: string;
    active: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());

  // Map brands to categories from the config response  curl -X 'GET' \ 'https://dev-auth.waffyapp.com/api/payment-types' \
  const paymentTypeIds: Record<string, number> = useMemo(() => {
    return {
      BANK_TRANSFER: 4,
      MASTER: 2,
      VISA: 2,
      AMEX: 2,
      MADA: 2,
      TABBY: 3,
      NEO: 5,
      TAMARA: 6,
    };
  }, []);
  // Define table columns
  const baseColumns = [
    { type: "tierName", value: t("lbl_tier_name") },
    { type: "from", value: t("lbl_from") },
    { type: "to", value: t("lbl_to") },
    {
      type: "bankTransfer",
      value: t("lbl_bank_transfer_amount"),
    },
    { type: "visa", value: t("lbl_visa") },
    { type: "mastercard", value: t("lbl_master_card") },
    { type: "amex", value: t("lbl_amex") },
    { type: "mada", value: t("lbl_mada") },
    { type: "tabby", value: t("lbl_tabby_amount") },
    { type: "neo", value: t("lbl_neo_amount") },
    { type: "tamara", value: t("lbl_tamara_amount") },
  ];

  const columns = isEditable
    ? [...baseColumns, { type: "actions", value: t("lbl_actions") }]
    : baseColumns;

  const isCreateMatch = matchPath(
    { path: ROUTES.CREATE_COMPLEX_CONTRACT },
    location.pathname
  );

  useEffect(() => {
    const fetchOrganizationTiers = async () => {
      if (!organization?.code || hasFetched) return;
      setIsLoading(true);
      try {
        const tiersRes = await getOrganizationTiers(organization?.code);

        setTemplateId(tiersRes?.data?.id);
        if (tiersRes?.data) {
          const metadata = {
            name: tiersRes.data.name,
            description: tiersRes.data.description,
            active: tiersRes.data.active,
          };
          setTemplateMetadata(metadata);
          setInitialMetadata(metadata);
        }

        const getFeeDisplay = (
          tier: IOrganizationFeeTier,
          brand: EPaymentBrand
        ) => {
          const pt = tier?.paymentTypes?.find(
            (p: IOrganizationPaymentFee) => p.brand === brand
          );
          if (pt === undefined || pt.fees === null || pt.fees === undefined)
            return "-";

          if (pt.feeType === "PERCENTAGE") {
            return `${(pt.fees * 100).toFixed(1)}%`;
          }
          return `${pt.fees} ${t("lbl_saudi_currency")}`;
        };

        const mappedTiers: TierInfo[] =
          tiersRes?.data?.tiers?.map((tier: IOrganizationFeeTier) => ({
            id: tier?.id,
            name: tier?.name,
            from: tier?.from,
            to: tier?.to || "-",
            rawPaymentTypes: tier?.paymentTypes,
            bankTransfer: getFeeDisplay(tier, EPaymentBrand.BANK_TRANSFER),
            visa: getFeeDisplay(tier, EPaymentBrand.VISA),
            mastercard: getFeeDisplay(tier, EPaymentBrand.MASTER),
            amex: getFeeDisplay(tier, EPaymentBrand.AMEX),
            mada: getFeeDisplay(tier, EPaymentBrand.MADA),
            tabby: getFeeDisplay(tier, EPaymentBrand.TABBY),
            neo: getFeeDisplay(tier, EPaymentBrand.NEO),
            tamara: getFeeDisplay(tier, EPaymentBrand.TAMARA),
          })) || [];

        setTiers(mappedTiers);
        setInitialTiers(JSON.parse(JSON.stringify(mappedTiers)));
        setHasUnsavedChanges(false);
        setHasFetched(true);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizationTiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.code, t]);

  const handleFieldChange = (
    rowIndex: number,
    field: keyof TierInfo,
    value: string | number
  ) => {
    const updatedTiers = [...tiers];
    let finalValue = value;

    if (field === "from") {
      const fromVal = Number(value) || 0;
      const toVal = updatedTiers[rowIndex].to;
      if (toVal !== "-" && Number(toVal) < fromVal + 1) {
        updatedTiers[rowIndex].to = fromVal + 1;
      }
    }

    if (field === "to") {
      const fromValue = Number(updatedTiers[rowIndex].from) || 0;
      if (value === "" || value === "-") {
        finalValue = "-";
      } else {
        const strVal = value.toString();
        const numTo = Number(strVal);

        if (strVal.endsWith(".")) {
          finalValue = strVal;
        } else if (numTo === 0) {
          finalValue = "-";
        } else if (!isNaN(numTo) && numTo < fromValue + 1) {
          finalValue = fromValue + 1;
        } else {
          finalValue = strVal;
        }
      }
    }

    updatedTiers[rowIndex] = {
      ...updatedTiers[rowIndex],
      [field]: finalValue,
    };
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setHasUnsavedChanges(true);
  };

  const handleAddTier = () => {
    const newTier: TierInfo = {
      name: "",
      from: 0,
      to: "-",
      bankTransfer: `0 ${t("lbl_saudi_currency")}`,
      visa: `0%`,
      mastercard: `0%`,
      amex: `0%`,
      mada: `0%`,
      tabby: `0 ${t("lbl_saudi_currency")}`,
      neo: `0 ${t("lbl_saudi_currency")}`,
      tamara: `0 ${t("lbl_saudi_currency")}`,
    };
    const updatedTiers = [...tiers, newTier];
    const newIndex = updatedTiers.length - 1;

    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setEditingRows((prev) => new Set(prev).add(newIndex));
    setHasUnsavedChanges(true);
  };

  const handleToggleEdit = (rowIndex: number) => {
    setEditingRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedTiers = tiers.filter((_, index) => index !== rowIndex);
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);

    // Also remove from editing rows if it was there
    setEditingRows((prev) => {
      const next = new Set(prev);
      next.delete(rowIndex);
      // We need to adjust other indexes if they shifted
      const adjusted = new Set<number>();
      prev.forEach((idx) => {
        if (idx < rowIndex) adjusted.add(idx);
        if (idx > rowIndex) adjusted.add(idx - 1);
      });
      return adjusted;
    });
    setHasUnsavedChanges(true);
  };

  const handleToggleUnit = (rowIndex: number, field: keyof TierInfo) => {
    const updatedTiers = [...tiers];
    const fieldValue = updatedTiers[rowIndex][field];
    if (typeof fieldValue === "function") return;

    const { numValue, unit } = parseValue(fieldValue as string | number);
    const newUnit = unit === "%" ? t("lbl_saudi_currency") : "%";

    updatedTiers[rowIndex] = {
      ...updatedTiers[rowIndex],
      [field]:
        newUnit === "%"
          ? `${numValue}%`
          : `${numValue} ${t("lbl_saudi_currency")}`,
    };
    setTiers(updatedTiers);
    onTiersChange?.(updatedTiers);
    setHasUnsavedChanges(true);
  };

  const isDirty = useMemo(() => {
    if (!hasUnsavedChanges) return false;

    const tiersChanged = JSON.stringify(tiers) !== JSON.stringify(initialTiers);
    const metadataChanged =
      templateMetadata?.name !== initialMetadata?.name ||
      templateMetadata?.description !== initialMetadata?.description ||
      templateMetadata?.active !== initialMetadata?.active;

    return tiersChanged || metadataChanged;
  }, [
    tiers,
    initialTiers,
    hasUnsavedChanges,
    templateMetadata,
    initialMetadata,
  ]);

  const handleSave = async () => {
    if (!organization || !isDirty) return;

    try {
      setIsLoading(true);

      // 1. Check if template metadata or tiers (count/name/range) changed
      const currentTemplateTiers = tiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        from: Number(tier.from) || 0,
        to: tier.to === "-" ? null : Number(tier.to) || 0,
      }));

      const initialTemplateTiers = initialTiers.map((tier) => ({
        id: tier.id,
        name: tier.name,
        from: Number(tier.from) || 0,
        to: tier.to === "-" ? null : Number(tier.to) || 0,
      }));

      // Detect any change in metadata OR tiers (including count/deletions/renames)
      const templateMetadataChanged =
        JSON.stringify(currentTemplateTiers) !==
          JSON.stringify(initialTemplateTiers) ||
        templateMetadata?.name !== initialMetadata?.name ||
        templateMetadata?.description !== initialMetadata?.description ||
        templateMetadata?.active !== initialMetadata?.active;

      let updatedServerTiers = initialTiers.map((t) => ({
        id: t.id as number,
        name: t.name,
        from: Number(t.from) || 0,
        to: t.to === "-" ? null : (Number(t.to) as number),
        paymentTypes: t.rawPaymentTypes || [],
      }));

      if (templateMetadataChanged && templateId) {
        const templateRes = await updateFeeTemplate(Number(templateId), {
          name: templateMetadata?.name || "",
          description: templateMetadata?.description || "",
          active: templateMetadata?.active ?? true,
          tiers: currentTemplateTiers as IOrganizationFeeTier[],
        });
        updatedServerTiers = templateRes?.data?.tiers || [];
      }

      const activeTemplateId = templateId;

      // 2. Identify and collect payment type fees (New vs Existing)
      const feesToSync: IOrganizationPaymentFee[] = [];
      const feesToAdd: IOrganizationPaymentFee[] = [];

      const brands: { [key: string]: IOrganizationPaymentBrand } = {
        BANK_TRANSFER: "BANK_TRANSFER",
        VISA: "VISA",
        MASTER: "MASTER",
        AMEX: "AMEX",
        MADA: "MADA",
        TABBY: "TABBY",
        NEO: "NEO",
        TAMARA: "TAMARA",
      };

      tiers.forEach((localTier) => {
        // 1. Only process tiers that have actual changes
        const initialTier = initialTiers.find((it) => it.id === localTier.id);
        const isTierModified =
          !initialTier ||
          JSON.stringify(localTier) !== JSON.stringify(initialTier);

        if (!isTierModified) return;

        // 2. Match with the newly updated server tier to get the correct tierId
        const serverTier = localTier.id
          ? updatedServerTiers.find((st) => st.id === localTier.id)
          : updatedServerTiers.find(
              (st) => Number(st.from) === Number(localTier.from)
            );

        const tierId = serverTier?.id;
        if (!tierId) return;

        Object.entries(brands).forEach(([brandKey, brandValue]) => {
          const fieldMap: {
            [key in IOrganizationPaymentBrand]?: keyof TierInfo;
          } = {
            BANK_TRANSFER: "bankTransfer",
            VISA: "visa",
            MASTER: "mastercard",
            AMEX: "amex",
            MADA: "mada",
            TABBY: "tabby",
            NEO: "neo",
            TAMARA: "tamara",
          };

          const field = fieldMap[brandValue];
          if (!field) return;

          const currentVal = localTier[field] as string;
          const initialVal = initialTier
            ? (initialTier[field] as string)
            : null;

          if (currentVal === initialVal) return;

          const { numValue, unit } = parseValue(currentVal);
          const paymentTypeId = paymentTypeIds[brandKey];

          if (paymentTypeId) {
            // Check if this payment type already existed to get its ID
            const existingFeeRecord = localTier.rawPaymentTypes?.find(
              (pt) => pt.brand === brandValue
            );

            const feeObj: IOrganizationPaymentFee = {
              ...(existingFeeRecord?.id ? { id: existingFeeRecord.id } : {}),
              feeTemplateId: Number(activeTemplateId),
              feeTemplateTierId: tierId,
              paymentTypeId: paymentTypeId,
              fees:
                unit === "%"
                  ? (Number(numValue) || 0) / 100
                  : Number(numValue) || 0,
              feeType: (unit === "%"
                ? "PERCENTAGE"
                : "FLAT") as IOrganizationFeeType,
              brand: brandValue,
            };

            if (existingFeeRecord?.id) {
              feesToSync.push(feeObj);
            } else {
              feesToAdd.push(feeObj);
            }
          }
        });
      });

      // 3. Save fees: POST for new records, PUT for existing records (Full-state synchronization)
      const promises: Promise<IBaseApiResponse>[] = [];

      if (feesToAdd.length > 0) {
        promises.push(
          addBulkOrganizationPaymentTypeFee(organization.code, feesToAdd)
        );
      }

      if (feesToSync.length > 0) {
        promises.push(
          updateOrganizationPaymentTypeFees(organization.code, {
            paymentTypes: feesToSync,
          })
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Reset dirty state explicitly
      setHasUnsavedChanges(false);
      setInitialTiers(JSON.parse(JSON.stringify(tiers)));
      if (templateMetadata) setInitialMetadata({ ...templateMetadata });

      // Refresh tiers to get updated IDs and data from server
      setHasFetched(false);
      setEditingRows(new Set());
      onSave?.(tiers);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const parseValue = (value: string | number) => {
    if (typeof value === "number")
      return { numValue: value.toString(), unit: "" };

    const valStr = value?.toString().trim() || "";
    if (valStr === "" || valStr === "-") return { numValue: "", unit: "" };

    // Remove commas from numbers like "100,000"
    const cleanValue = valStr.replace(/,/g, "");
    const match = cleanValue.match(/^([\d.]*)\s*(.*)$/);
    if (match) {
      return {
        numValue: match[1],
        unit: match[2],
      };
    }
    return { numValue: "", unit: "" };
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  // Render cell based on column type and data
  const renderCell = (columnType: string, row: TierInfo, rowIndex: number) => {
    const isEditing = editingRows.has(rowIndex);

    if (columnType === "actions") {
      return (
        <div className={styles.actionsContainer}>
          <span
            className={classNames(styles.actionText, styles.editText)}
            onClick={() => handleToggleEdit(rowIndex)}>
            {isEditing ? t("btn_done_per_row") : t("btn_edit_per_row")}
          </span>
          <span
            className={classNames(styles.actionText, styles.deleteText, {
              [styles.deleteBlockedText]: tiers?.length <= 1,
            })}
            onClick={() => tiers?.length > 1 && handleDeleteRow(rowIndex)}>
            {t("btn_delete_per_row")}
          </span>
        </div>
      );
    }

    if (isEditing) {
      const fieldValue = row[columnType as keyof TierInfo];
      if (typeof fieldValue === "function") return null;

      const isFeeField = [
        "bankTransfer",
        "visa",
        "mastercard",
        "amex",
        "mada",
        "tabby",
        "neo",
        "tamara",
      ].includes(columnType);

      const { numValue, unit: parsedUnit } = parseValue(
        fieldValue as string | number
      );
      let unit = parsedUnit;

      // Default to Flat (SAR) for fee fields if unit is missing
      if (isFeeField && !unit) {
        unit = t("lbl_saudi_currency");
      }

      const isPercent = unit === "%";
      const isSAR = unit === t("lbl_saudi_currency");

      const hasUnit = unit !== "";

      return (
        <div className={styles.editableCellContainer}>
          <TextInput
            type={
              columnType === "tierName" ? InputType.DEFAULT : InputType.NUMBER
            }
            value={columnType === "tierName" ? row.name : numValue}
            maxValue={isPercent ? 100 : undefined}
            maxDecimals={1}
            handleChange={(e) =>
              handleFieldChange(
                rowIndex,
                (columnType === "tierName"
                  ? "name"
                  : columnType) as keyof TierInfo,
                columnType === "tierName"
                  ? e.target.value
                  : isPercent
                  ? `${e.target.value}%`
                  : isSAR
                  ? `${e.target.value} ${t("lbl_saudi_currency")}`
                  : e.target.value
              )
            }
            containerWidth="100%"
            style={{
              textAlign: "right",
              paddingLeft: hasUnit ? "35px" : "8px",
              paddingRight: "8px",
              minWidth: "unset",
              height: "32px",
            }}
          />
          {isFeeField && (
            <div
              className={styles.unitToggle}
              onClick={() =>
                handleToggleUnit(rowIndex, columnType as keyof TierInfo)
              }>
              {unit}
            </div>
          )}
          {!isFeeField &&
            !["tierName", "from", "to", "actions"].includes(columnType) && (
              <div className={styles.unitStatic}>{unit}</div>
            )}
        </div>
      );
    }

    switch (columnType) {
      case "tierName":
        return <span className={styles.tierName}>{row.name}</span>;
      case "from":
        return (
          <span className={styles.amountValue}>
            {row?.from?.toLocaleString()}
          </span>
        );
      case "to":
        return (
          <span
            className={classNames(
              styles.amountValue,
              styles.activeAmountValue
            )}>
            {row?.to?.toLocaleString()}
          </span>
        );
      case "bankTransfer":
        return (
          <span
            className={styles.percentValue}
            style={
              row.bankTransfer.includes("%") ? undefined : { direction: "rtl" }
            }>
            {row.bankTransfer}
          </span>
        );
      case "visa":
        return (
          <span
            className={styles.percentValue}
            style={row.visa.includes("%") ? undefined : { direction: "rtl" }}>
            {row.visa}
          </span>
        );
      case "mastercard":
        return (
          <span
            className={styles.percentValue}
            style={
              row.mastercard.includes("%") ? undefined : { direction: "rtl" }
            }>
            {row.mastercard}
          </span>
        );
      case "amex":
        return (
          <span
            className={styles.percentValue}
            style={row.amex.includes("%") ? undefined : { direction: "rtl" }}>
            {row.amex}
          </span>
        );
      case "mada":
        return (
          <span
            className={styles.percentValue}
            style={row.mada.includes("%") ? undefined : { direction: "rtl" }}>
            {row.mada}
          </span>
        );
      case "tabby":
        return (
          <span
            className={styles.percentValue}
            style={row.tabby.includes("%") ? undefined : { direction: "rtl" }}>
            {row.tabby}
          </span>
        );
      case "neo":
        return (
          <span
            className={styles.percentValue}
            style={row.neo.includes("%") ? undefined : { direction: "rtl" }}>
            {row.neo}
          </span>
        );
      case "tamara":
        return (
          <span
            className={styles.percentValue}
            style={row.tamara.includes("%") ? undefined : { direction: "rtl" }}>
            {row.tamara}
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div className={styles.titleWrapper}>
          <div className={styles.textWrapper}>
            <h3
              className={styles.title}
              style={{ fontSize: isCreateMatch ? "24px" : "18px" }}>
              {isCreateMatch
                ? t("lbl_payment_methods_fees")
                : t("lbl_tiers_info_title")}
            </h3>
            <p className={styles.subtitle}>{t("lbl_tiers_info_subtitle")}</p>
          </div>
          {isEditable && (
            <PrimaryButton
              buttonType={PrimaryButtonType.PRIMARY_DARK}
              size={PrimaryButtonSize.MEDIUM}
              style={{ width: "154px", height: "35px" }}
              onClick={handleAddTier}>
              {t("btn_add_tier")}
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className={styles.tableContainer}>
        <TiersBusinessTable
          columns={columns}
          rows={tiers}
          renderCell={(columnType, row, rowIndex) =>
            renderCell(columnType, row as TierInfo, rowIndex)
          }
        />
      </div>
      {isEditable && (
        <div className={styles.saveButtonWrapper}>
          <PrimaryButton
            buttonType={PrimaryButtonType.PRIMARY_DARK}
            size={PrimaryButtonSize.MEDIUM}
            width="200px"
            disabled={!isDirty}
            onClick={handleSave}>
            {t("btn_save_changes")}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

// Custom wrapper for BusinessTable specifically for TiersInformation
const TiersBusinessTable = ({
  columns,
  rows,
  renderCell,
}: {
  columns: { type: string; value: string }[];
  rows: TierInfo[];
  renderCell: (
    column: string,
    row: TierInfo,
    rowIndex: number
  ) => React.ReactNode;
}) => {
  return (
    <div
      className={classNames(styles.tiersTableWrapper, {
        [styles.editableTable]: columns.length > 11,
      })}>
      <BusinessTable
        columns={columns}
        rows={rows}
        renderCell={(column, row, rowIndex) =>
          renderCell(column, row as TierInfo, rowIndex)
        }
      />
    </div>
  );
};

export default TiersInformation;
